import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import { getLocalizedNumber } from '../hooks/useTours'
import { MapPin, Phone, Mail, MessageCircle, Facebook, Twitter, Instagram, Youtube } from 'lucide-react'

export function Footer() {
  const { t } = useTranslation()
  const { isRTL, language } = useLanguage()
  const currentYear = new Date().getFullYear()
  
  // Replace the year in copyright text with current year
  const copyrightText = t('footer.copyright').replace(/\d{4}/, currentYear.toString())
  
  return (
    <footer className="bg-background border-t border-border">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h3 className="text-2xl font-bold text-foreground">{t('footer.company.title')}</h3>
            <p className="text-sm text-muted-foreground mb-2">{t('footer.company.subtitle')}</p>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t('footer.company.description')}
            </p>
            <div className="flex space-x-4">
              <motion.a
                whileHover={{ scale: 1.1 }}
                href="https://www.facebook.com/karwanekarbalaosham/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-secondary rounded-full hover:bg-blue-600 transition-colors duration-300 group"
              >
                <Facebook className="h-5 w-5 text-foreground group-hover:text-white transition-colors" />
              </motion.a>
              {/* <motion.a
                whileHover={{ scale: 1.1 }}
                href="#"
                className="p-2 bg-secondary rounded-full hover:bg-primary transition-colors duration-300"
              >
                <Twitter className="h-5 w-5 text-foreground" />
              </motion.a> */}
              <motion.a
                whileHover={{ scale: 1.1 }}
                href="https://www.instagram.com/karwanekarbalaosham/?e=0ced2763-7b88-4f6f-ace2-e29cea5c843b&g=5"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-secondary rounded-full hover:bg-pink-600 transition-colors duration-300 group"
              >
                <Instagram className="h-5 w-5 text-foreground group-hover:text-white transition-colors" />
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.1 }}
                href="https://www.youtube.com/@karwanekarbalaosham430"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-secondary rounded-full hover:bg-red-600 transition-colors duration-300 group"
              >
                <Youtube className="h-5 w-5 text-foreground group-hover:text-white transition-colors" />
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.1 }}
                href="https://wa.me/923349985065"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-secondary rounded-full hover:bg-green-500 transition-colors duration-300 group"
              >
                <MessageCircle className="h-5 w-5 text-foreground group-hover:text-white transition-colors" />
              </motion.a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h4 className={`text-lg font-semibold text-foreground ${isRTL ? 'text-right' : 'text-left'}`}>{t('footer.links.title')}</h4>
            <ul className={`space-y-2 ${isRTL ? 'text-right' : 'text-left'}`}>
              {[
                { name: t('footer.links.home'), href: '/' },
                { name: t('footer.links.contact'), href: '/contact' }
              ].map((link, index) => (
                <li key={index}>
                  <motion.a
                    href={link.href}
                    className="text-muted-foreground hover:text-foreground transition-colors duration-300 text-sm block"
                    whileHover={{ x: isRTL ? -5 : 5 }}
                  >
                    {link.name}
                  </motion.a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-foreground">{t('footer.contact.title')}</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-muted-foreground text-sm" dangerouslySetInnerHTML={{ __html: t('footer.contact.address') }} />
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                <div>
                  <p className="text-muted-foreground text-sm phone">{getLocalizedNumber(t('footer.contact.phone'), language)}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                <p className="text-muted-foreground text-sm">{t('footer.contact.email')}</p>
              </div>
              
              <div className="flex items-center space-x-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0" />
                <a 
                  href="https://wa.me/923349985065" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm phone"
                >
                  {getLocalizedNumber(t('footer.contact.phone'), language)}
                </a>
              </div>
            </div>

            {/* Newsletter Signup - Hidden for now */}
            {/* <div className="mt-6">
              <h5 className="text-foreground font-medium mb-2">Stay Updated</h5>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-3 py-2 bg-secondary border border-border rounded-l-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-r-lg hover:bg-primary/90 transition-colors duration-300"
                >
                  Subscribe
                </motion.button>
              </div>
            </div> */}
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="border-t border-border mt-12 pt-8 flex flex-col md:flex-row justify-between items-center"
        >
          <p className="text-muted-foreground text-sm">
            {copyrightText}
          </p>
          <p className="text-muted-foreground text-sm mt-2 md:mt-0">
            {t('footer.madeWith')}
          </p>
        </motion.div>
      </div>
    </footer>
  )
}
